"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var jsx_runtime_1 = require("react/jsx-runtime");
var react_1 = require("react");
var utils_1 = require("@prezzee/utils");
// Inspired by https://github.com/focus-trap/tabbable
var candidatesSelector = [
    'input',
    'select',
    'textarea',
    'a[href]',
    'button',
    '[tabindex]',
    'audio[controls]',
    'video[controls]',
    '[contenteditable]:not([contenteditable="false"])',
].join(',');
function getTabIndex(node) {
    var tabindexAttr = parseInt(node.getAttribute('tabindex'), 10);
    if (!Number.isNaN(tabindexAttr)) {
        return tabindexAttr;
    }
    // Browsers do not return `tabIndex` correctly for contentEditable nodes;
    // https://bugs.chromium.org/p/chromium/issues/detail?id=661108&q=contenteditable%20tabindex&can=2
    // so if they don't have a tabindex attribute specifically set, assume it's 0.
    // in Chrome, <details/>, <audio controls/> and <video controls/> elements get a default
    //  `tabIndex` of -1 when the 'tabindex' attribute isn't specified in the DOM,
    //  yet they are still part of the regular tab order; in FF, they get a default
    //  `tabIndex` of 0; since Chrome still puts those elements in the regular tab
    //  order, consider their tab index to be 0.
    if (node.contentEditable === 'true' ||
        ((node.nodeName === 'AUDIO' || node.nodeName === 'VIDEO' || node.nodeName === 'DETAILS') &&
            node.getAttribute('tabindex') === null)) {
        return 0;
    }
    return node.tabIndex;
}
function isNonTabbableRadio(node) {
    if (node.tagName !== 'INPUT' || node.type !== 'radio') {
        return false;
    }
    if (!node.name) {
        return false;
    }
    var getRadio = function (selector) {
        return node.ownerDocument.querySelector("input[type=\"radio\"]".concat(selector));
    };
    var roving = getRadio("[name=\"".concat(node.name, "\"]:checked"));
    if (!roving) {
        roving = getRadio("[name=\"".concat(node.name, "\"]"));
    }
    return roving !== node;
}
function isNodeMatchingSelectorFocusable(node) {
    if (node.disabled ||
        (node.tagName === 'INPUT' && node.type === 'hidden') ||
        isNonTabbableRadio(node)) {
        return false;
    }
    return true;
}
function defaultGetTabbable(root) {
    var regularTabNodes = [];
    var orderedTabNodes = [];
    Array.from(root.querySelectorAll(candidatesSelector)).forEach(function (node, i) {
        var nodeTabIndex = getTabIndex(node);
        if (nodeTabIndex === -1 || !isNodeMatchingSelectorFocusable(node)) {
            return;
        }
        if (nodeTabIndex === 0) {
            regularTabNodes.push(node);
        }
        else {
            orderedTabNodes.push({
                documentOrder: i,
                tabIndex: nodeTabIndex,
                node: node,
            });
        }
    });
    return orderedTabNodes
        .sort(function (a, b) {
        return a.tabIndex === b.tabIndex ? a.documentOrder - b.documentOrder : a.tabIndex - b.tabIndex;
    })
        .map(function (a) { return a.node; })
        .concat(regularTabNodes);
}
function defaultIsEnabled() {
    return true;
}
/**
 * Utility component that locks focus inside the component.
 */
function TrapFocus(props) {
    var children = props.children, _a = props.disableAutoFocus, disableAutoFocus = _a === void 0 ? false : _a, _b = props.disableEnforceFocus, disableEnforceFocus = _b === void 0 ? false : _b, _c = props.disableRestoreFocus, disableRestoreFocus = _c === void 0 ? false : _c, _d = props.getTabbable, getTabbable = _d === void 0 ? defaultGetTabbable : _d, _e = props.isEnabled, isEnabled = _e === void 0 ? defaultIsEnabled : _e, open = props.open;
    var ignoreNextEnforceFocus = (0, react_1.useRef)();
    var sentinelStart = (0, react_1.useRef)(null);
    var sentinelEnd = (0, react_1.useRef)(null);
    var nodeToRestore = (0, react_1.useRef)(null);
    var reactFocusEventTarget = (0, react_1.useRef)(null);
    // This variable is useful when disableAutoFocus is true.
    // It waits for the active element to move into the component to activate.
    var activated = (0, react_1.useRef)(false);
    var rootRef = (0, react_1.useRef)(null);
    // @ts-ignore
    var handleRef = (0, utils_1.useForkRef)(children.ref, rootRef);
    var lastKeydown = (0, react_1.useRef)(null);
    (0, react_1.useEffect)(function () {
        // We might render an empty child.
        if (!open || !rootRef.current) {
            return;
        }
        activated.current = !disableAutoFocus;
    }, [disableAutoFocus, open]);
    (0, react_1.useEffect)(function () {
        // We might render an empty child.
        if (!open || !rootRef.current) {
            return;
        }
        var doc = (0, utils_1.ownerDocument)(rootRef.current);
        if (!rootRef.current.contains(doc.activeElement)) {
            if (!rootRef.current.hasAttribute('tabIndex')) {
                if (process.env.NODE_ENV !== 'production') {
                    console.error([
                        'The modal content node does not accept focus.',
                        'For the benefit of assistive technologies, ' +
                            'the tabIndex of the node is being set to "-1".',
                    ].join('\n'));
                }
                rootRef.current.setAttribute('tabIndex', '-1');
            }
            if (activated.current) {
                rootRef.current.focus();
            }
        }
        return function () {
            // restoreLastFocus()
            if (!disableRestoreFocus) {
                // In IE11 it is possible for document.activeElement to be null resulting
                // in nodeToRestore.current being null.
                // Not all elements in IE11 have a focus method.
                // Once IE11 support is dropped the focus() call can be unconditional.
                if (nodeToRestore.current && nodeToRestore.current.focus) {
                    ignoreNextEnforceFocus.current = true;
                    nodeToRestore.current.focus();
                }
                nodeToRestore.current = null;
            }
        };
        // Missing `disableRestoreFocus` which is fine.
        // We don't support changing that prop on an open TrapFocus
    }, [open]);
    (0, react_1.useEffect)(function () {
        // We might render an empty child.
        if (!open || !rootRef.current) {
            return;
        }
        var doc = (0, utils_1.ownerDocument)(rootRef.current);
        var contain = function (nativeEvent) {
            var _a, _b;
            var rootElement = rootRef.current;
            // Cleanup functions are executed lazily in React 17.
            // Contain can be called between the component being unmounted and its cleanup function being run.
            if (rootElement === null) {
                return;
            }
            if (!doc.hasFocus() ||
                disableEnforceFocus ||
                !isEnabled() ||
                ignoreNextEnforceFocus.current) {
                ignoreNextEnforceFocus.current = false;
                return;
            }
            if (!rootElement.contains(doc.activeElement)) {
                // if the focus event is not coming from inside the children's react tree, reset the refs
                if ((nativeEvent && reactFocusEventTarget.current !== nativeEvent.target) ||
                    doc.activeElement !== reactFocusEventTarget.current) {
                    reactFocusEventTarget.current = null;
                }
                else if (reactFocusEventTarget.current !== null) {
                    return;
                }
                if (!activated.current) {
                    return;
                }
                var tabbable = [];
                if (doc.activeElement === sentinelStart.current ||
                    doc.activeElement === sentinelEnd.current) {
                    tabbable = getTabbable(rootRef.current);
                }
                if (tabbable.length > 0) {
                    var isShiftTab = Boolean(((_a = lastKeydown.current) === null || _a === void 0 ? void 0 : _a.shiftKey) && ((_b = lastKeydown.current) === null || _b === void 0 ? void 0 : _b.key) === 'Tab');
                    var focusNext = tabbable[0];
                    var focusPrevious = tabbable[tabbable.length - 1];
                    if (isShiftTab) {
                        focusPrevious.focus();
                    }
                    else {
                        focusNext.focus();
                    }
                }
                else {
                    rootElement.focus();
                }
            }
        };
        var loopFocus = function (nativeEvent) {
            var _a;
            lastKeydown.current = nativeEvent;
            if (disableEnforceFocus || !isEnabled() || nativeEvent.key !== 'Tab') {
                return;
            }
            // Make sure the next tab starts from the right place.
            // doc.activeElement referes to the origin.
            if (doc.activeElement === rootRef.current && nativeEvent.shiftKey) {
                // We need to ignore the next contain as
                // it will try to move the focus back to the rootRef element.
                ignoreNextEnforceFocus.current = true;
                (_a = sentinelEnd.current) === null || _a === void 0 ? void 0 : _a.focus();
            }
        };
        doc.addEventListener('focusin', contain);
        doc.addEventListener('keydown', loopFocus, true);
        // With Edge, Safari and Firefox, no focus related events are fired when the focused area stops being a focused area.
        // e.g. https://bugzilla.mozilla.org/show_bug.cgi?id=559561.
        // Instead, we can look if the active element was restored on the BODY element.
        //
        // The whatwg spec defines how the browser should behave but does not explicitly mention any events:
        // https://html.spec.whatwg.org/multipage/interaction.html#focus-fixup-rule.
        var interval = setInterval(function () {
            var _a;
            if (((_a = doc === null || doc === void 0 ? void 0 : doc.activeElement) === null || _a === void 0 ? void 0 : _a.tagName) === 'BODY') {
                contain();
            }
        }, 50);
        return function () {
            clearInterval(interval);
            doc.removeEventListener('focusin', contain);
            doc.removeEventListener('keydown', loopFocus, true);
        };
    }, [disableAutoFocus, disableEnforceFocus, disableRestoreFocus, isEnabled, open, getTabbable]);
    var onFocus = function (event) {
        if (nodeToRestore.current === null) {
            nodeToRestore.current = event.relatedTarget;
        }
        activated.current = true;
        reactFocusEventTarget.current = event.target;
        var childrenPropsHandler = children.props.onFocus;
        if (childrenPropsHandler) {
            childrenPropsHandler(event);
        }
    };
    var handleFocusSentinel = function (event) {
        if (nodeToRestore.current === null) {
            nodeToRestore.current = event.relatedTarget;
        }
        activated.current = true;
    };
    return ((0, jsx_runtime_1.jsxs)(react_1.Fragment, { children: [(0, jsx_runtime_1.jsx)("div", { tabIndex: 0, onFocus: handleFocusSentinel, ref: sentinelStart, "data-test": "sentinelStart" }), (0, react_1.cloneElement)(children, { ref: handleRef, onFocus: onFocus }), (0, jsx_runtime_1.jsx)("div", { tabIndex: 0, onFocus: handleFocusSentinel, ref: sentinelEnd, "data-test": "sentinelEnd" })] }));
}
exports.default = TrapFocus;
//# sourceMappingURL=TrapFocus.js.map